import { ArrowUpRight, HandWaving, UploadSimple } from "@phosphor-icons/react";
import clsx from "clsx";
import { type DragEvent, useRef } from "react";
import { toast } from "react-toastify";

import Loader from "common/components/Loader";
import { getWhitelabelCustomization } from "common/helpers/utils";

import useParseCsv from "../../../datahooks/useParseCsv";

import type { CsvState } from "../../../types";

const MAX_FILE_BYTES = 50 * 1024 * 1024;

function decodeUtf8OrWin1252(buf: ArrayBuffer | ArrayBufferView): string {
  const bytes =
    buf instanceof Uint8Array ? buf : new Uint8Array(buf as ArrayBuffer);

  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    try {
      return new TextDecoder("windows-1252").decode(bytes);
    } catch {
      return new TextDecoder("utf-8").decode(bytes);
    }
  }
}

export default function CsvUpload({
  onSuccess,
}: {
  onSuccess: (csvState: CsvState, fileName: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { parseCsv, isParsingCsv } = useParseCsv();

  function handleClick() {
    if (!isParsingCsv) fileInputRef.current?.click();
  }

  async function parseFile(file: File) {
    if (!file) {
      toast.error("No file selected");
      return;
    }

    if (file.type !== "text/csv") {
      toast.error(
        "Upload failed. Please make sure you are uploading a CSV file.",
      );
      return;
    }

    if (file.size > MAX_FILE_BYTES) {
      toast.error("File exceeds maximum size of 50MB");
      return;
    }

    try {
      const buf = await file.arrayBuffer();
      const text = decodeUtf8OrWin1252(buf);
      const data = await parseCsv({ csv: text });

      onSuccess(data, file.name);

      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    } catch {
      toast.error(
        "Error reading or parsing the file. Please check the CSV and try again.",
      );
    }
  }

  function onDrop(e: DragEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (isParsingCsv) return;

    parseFile(e.dataTransfer.files[0]);

    e.currentTarget.setAttribute("data-dragging", "false");
  }

  function onDragOver(e: DragEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
  }

  function onDragEnter(e: DragEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.currentTarget.setAttribute("data-dragging", "true");
  }

  function onDragLeave(e: DragEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.currentTarget.setAttribute("data-dragging", "false");
  }

  return (
    <>
      <button
        type="button"
        className={clsx(
          "group flex px-6 py-14 mb-3 flex-col items-center rounded-3xl border border-dashed border-black-300 bg-black-50 transition-colors disabled:pointer-events-none",
          !isParsingCsv &&
            "data-[dragging=true]:border-purple-400 data-[dragging=true]:bg-purple-50 hover:border-purple-400 hover:bg-purple-50",
        )}
        onClick={handleClick}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        disabled={isParsingCsv}
        aria-disabled={isParsingCsv}
      >
        {isParsingCsv ? (
          <Loader iconSize={32} />
        ) : (
          <UploadSimple className="text-purple-500 size-8" />
        )}
        <p className="text-center text-button-md mt-3 mb-2.5">
          {isParsingCsv ? "Reading your file" : "Upload CSV document"}
        </p>
        <p className="text-center text-sm-medium text-black-500">
          {isParsingCsv
            ? "We're importing your contacts and matching up your columns"
            : "Drop file or click here to choose file"}
        </p>
      </button>

      <input
        ref={fileInputRef}
        id="csv-input"
        type="file"
        className="hidden"
        accept="text/csv"
        onChange={(e) => parseFile(e.currentTarget.files[0])}
      />

      <div className="justify-between flex items-center mb-5">
        <span className="text-xs-medium text-black-400">
          Supports file up to 50MB, maximum rows 10.000
        </span>
        {/*<Button variant="link-purple" size="sm">
          Enter rows manually
        </Button>*/}
      </div>

      <div className="rounded-xl flex items-center p-1 pr-3 border border-black-200 gap-x-1 shadow-sm">
        <div className="inline-flex size-5 shrink-0 items-center justify-center rounded-md bg-purple-100 text-purple-500 border border-purple-200">
          <HandWaving size={16} />
        </div>
        <p className="text-xs text-black-600">Learn more about</p>
        <a
          href={
            getWhitelabelCustomization()
              ? "https://help.lnconnect.me/en/articles/14433029-list-campaign-csv-upload-guide"
              : "https://help.aimfox.com/en/articles/10827433-list-campaign-csv-upload-guide"
          }
          target="_blank"
          rel="noreferrer"
          className="text-xs text-purple-600 gap-x-0.5 flex items-center hover:underline"
        >
          Lead Lists and .CSV formatting
          <ArrowUpRight className="size-4" />
        </a>
      </div>
    </>
  );
}
